import { z } from "zod";

/**
 * UUID-shaped identifier used for all entities. Matches what Postgres `uuid`
 * actually stores (any 8-4-4-4-12 hex value) — zod's strict RFC-4122 check
 * rejects seeded/test fixtures whose variant nibble is not 89ab.
 */
export const Id = z
  .string()
  .regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/, {
    message: "Invalid UUID",
  });
export type Id = z.infer<typeof Id>;

/** ISO-8601 timestamp string (UTC). */
export const IsoDateTime = z.iso.datetime();
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/** Money stored as integer minor units (satang). Default currency THB. */
export const Money = z.object({
  amountMinor: z.number().int().nonnegative(),
  currency: z.string().length(3).default("THB"),
});
export type Money = z.infer<typeof Money>;

/** Pagination metadata returned with list responses. */
export const PageMeta = z.object({
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type PageMeta = z.infer<typeof PageMeta>;

/** Standard query params for paginated list endpoints. */
export const PageQuery = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});
export type PageQuery = z.infer<typeof PageQuery>;

/**
 * Consistent API response envelope. Backend mirrors this shape.
 * `data` is null on error; `error` is null on success.
 */
export function apiResponse<T extends z.ZodType>(data: T) {
  return z.object({
    success: z.boolean(),
    data: data.nullable(),
    error: z.string().nullable(),
    meta: PageMeta.optional(),
  });
}
